import {
  Component,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthStateService } from '@dental-ui/auth';
import { ConfirmDialogService } from '@dental-ui/ui';
import { calculateInitials, getInitial } from '@dental-ui/util';
import { PORTAL_ADMIN_RESOURCES } from '@dental-ui/portal-dashboard';
import {
  LucideBoxes,
  LucideChevronDown,
  LucideChevronsLeft,
  LucideCpu,
  LucideFile,
  LucideKeyRound,
  LucideLayers,
  LucideLayoutDashboard,
  LucideListChecks,
  LucideLogOut,
  LucidePackage,
  LucidePanelsTopLeft,
  LucideSettings2,
  LucideShield,
  LucideShieldCheck,
  LucideSlidersHorizontal,
  LucideTag,
  LucideUsers,
} from '@lucide/angular';
import { Subscription, filter } from 'rxjs';

const SIDEBAR_COLLAPSED_KEY = 'dc-portal-sidebar-collapsed';
const SIDEBAR_COLLAPSE_MQ = '(max-width: 780px)';

export interface PortalMenuItem {
  label: string;
  route: string;
}

@Component({
  selector: 'lib-portal-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LucideBoxes,
    LucideChevronDown,
    LucideChevronsLeft,
    LucideCpu,
    LucideFile,
    LucideKeyRound,
    LucideLayers,
    LucideLayoutDashboard,
    LucideListChecks,
    LucideLogOut,
    LucidePackage,
    LucidePanelsTopLeft,
    LucideSettings2,
    LucideShield,
    LucideShieldCheck,
    LucideSlidersHorizontal,
    LucideTag,
    LucideUsers,
  ],
  templateUrl: './portal-shell.component.html',
  styleUrl: './portal-shell.component.scss',
})
export class PortalShellComponent implements OnDestroy {
  private readonly authStateService = inject(AuthStateService);
  private readonly router = inject(Router);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly platformId = inject(PLATFORM_ID);

  private routerSubscription?: Subscription;
  private sidebarViewportQuery?: MediaQueryList;
  private userSidebarCollapsed = false;

  readonly isUserDropdownOpen = signal(false);
  readonly userAvatarUrl = signal<string | null>(null);
  readonly avatarImageFailed = signal(false);
  readonly isSidebarCollapsed = signal(false);
  readonly isCompactViewport = signal(false);
  readonly currentUrl = signal(this.router.url);

  userFullName = '';
  userEmail = '';
  userInitials = '';

  readonly portalMenuItems: PortalMenuItem[] = [
    { label: 'Yönetim Paneli', route: '/' },
    ...PORTAL_ADMIN_RESOURCES.map((resource) => ({
      label: resource.title,
      route: `/${resource.route}`,
    })),
  ];

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.userSidebarCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
      this.sidebarViewportQuery = window.matchMedia(SIDEBAR_COLLAPSE_MQ);
      this.syncSidebarToViewport();
      this.sidebarViewportQuery.addEventListener('change', this.onSidebarViewportChange);
    }

    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects || event.url);
      });

    effect(() => {
      const user = this.authStateService.user();
      if (user?.firstName && user?.lastName) {
        const first = user.firstName.trim();
        const last = user.lastName.trim();
        this.userFullName = `${first} ${last}`;
        this.userInitials = calculateInitials(first, last, 'tr');
      } else if (user?.email) {
        this.userFullName = user.email;
        this.userInitials = getInitial(user.email).toUpperCase();
      } else {
        this.userFullName = '';
        this.userInitials = '';
      }

      this.userEmail = (user?.email || '').trim();

      const url = (user?.userAvatarUrl || '').trim();
      const byte = (user?.userAvatarByte || '').trim();
      const nextAvatar =
        url ||
        (byte
          ? byte.startsWith('data:')
            ? byte
            : `data:image/jpeg;base64,${byte}`
          : null);

      if (nextAvatar !== this.userAvatarUrl()) {
        this.avatarImageFailed.set(false);
        this.userAvatarUrl.set(nextAvatar);
      }
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.sidebarViewportQuery?.removeEventListener('change', this.onSidebarViewportChange);
  }

  private onSidebarViewportChange = (): void => {
    this.syncSidebarToViewport();
  };

  private syncSidebarToViewport(): void {
    const compact = !!this.sidebarViewportQuery?.matches;
    this.isCompactViewport.set(compact);
    if (compact) {
      this.isSidebarCollapsed.set(true);
      return;
    }
    this.isSidebarCollapsed.set(this.userSidebarCollapsed);
  }

  collapseSidebarForMobile(): void {
    if (!this.isCompactViewport()) {
      return;
    }
    this.isSidebarCollapsed.set(true);
  }

  onNavItemClick(): void {
    this.collapseSidebarForMobile();
  }

  toggleSidebarCollapsed(): void {
    this.isSidebarCollapsed.update((collapsed) => {
      const next = !collapsed;
      this.userSidebarCollapsed = next;
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      }
      return next;
    });
  }

  isActiveRoute(route: string): boolean {
    const currentUrl = (this.currentUrl().split('?')[0] || '').replace(/\/$/, '') || '/';
    const normalizedRoute = route.replace(/\/$/, '') || '/';
    if (normalizedRoute === '/') {
      return currentUrl === '/' || currentUrl === '';
    }
    return currentUrl === normalizedRoute || currentUrl.startsWith(`${normalizedRoute}/`);
  }

  toggleUserDropdown(): void {
    this.isUserDropdownOpen.update((open) => !open);
  }

  closeUserDropdown(): void {
    this.isUserDropdownOpen.set(false);
  }

  onAvatarImageError(): void {
    this.avatarImageFailed.set(true);
  }

  logout(): void {
    this.closeUserDropdown();
    this.confirmDialogService
      .confirm({
        title: 'Çıkış yap',
        message: 'Oturumu kapatmak istediğinize emin misiniz?',
        confirmText: 'Çıkış Yap',
        cancelText: 'Kal',
        confirmButtonType: 'danger',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.authStateService.logout();
        }
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu-container')) {
      this.closeUserDropdown();
    }
  }
}
